import React, { useState, useMemo, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { startOfToday, endOfToday, format } from 'date-fns';
import { Row, Container, Dropdown, DropdownButton, Col, ButtonGroup, FormControl, Button } from 'react-bootstrap';
import { Select } from 'antd';
import { DefaultRecordType } from 'rc-table/lib/interface';
import ExcelIcon from 'mdi-react/MicrosoftExcelIcon';
import history from 'browserHistory';
import { AxiosResponse } from 'axios';

import { instance as axios } from 'api/axios';
import {
  DirectorReportProps,
  DirectorReportTypes,
  OrderReportTypes,
  SecurityReportProps,
  SecurityReportTypes,
} from 'modules/security/reports/interfaces';
import ReportUnloading from 'modules/security/reports/ReportUnloading';
import { useDebounce, useModal } from 'modules/shared/hooks';
import ReportSpecialList from 'modules/security/reports/ReportSpecialList';
import YesNoModal from 'modules/shared/YesNoModal';
import PhotoGrid from 'modules/shared/FlightDetails/components/PhotoGrid';
import ErrorMsg from 'modules/shared/ErrorMsg';
import ReportLoading from 'modules/security/reports/ReportLoading';
import SmallSpinner from 'modules/shared/SmallSpinner';
import { RootState } from 'rootReducer';
import Report2359 from '../../reports/Report2359/Report2359';
import { datetimeInputFormat, secReportComment } from '../../reports/constants';
import { clearReportPhoto, getRepTripRequest } from './services/actions';
import { repPhotoSelector } from './services/selectors';
import styles from '../TripsTabContainer/TripsTab.module.scss';

const reportCompSecurity: { [key in SecurityReportTypes]: React.FC<SecurityReportProps> } = {
  [SecurityReportTypes.specialList]: ReportSpecialList,
  [SecurityReportTypes.report2359]: Report2359,
  [SecurityReportTypes.loading]: ReportLoading,
  [SecurityReportTypes.unloading]: ReportUnloading,
};

const directorCompSecurity: { [key in DirectorReportTypes]: React.FC<DirectorReportProps> } = {
  [DirectorReportTypes.specialList]: ReportSpecialList,
  [DirectorReportTypes.report2359]: Report2359,
  [DirectorReportTypes.loading]: ReportLoading,
  [DirectorReportTypes.unloading]: ReportUnloading,
};

const reportCompOrder: { [key in OrderReportTypes]: React.FC<SecurityReportProps> } = {
  [OrderReportTypes.specialList]: ReportSpecialList,
  [OrderReportTypes.report2359]: Report2359,
  [OrderReportTypes.loading]: ReportLoading,
  [OrderReportTypes.unloading]: ReportUnloading,
};

export const ReportsContainer: React.FC = () => {
  const dispatch = useDispatch();

  const [forceUpdate, setForceUpdate] = useState(false);
  const [reportComponent, setReportComponent] = useState<
    SecurityReportTypes | OrderReportTypes | DirectorReportTypes
  >();
  const [reportType, setReportType] = useState<SecurityReportTypes>();
  const [startDate, setStartDate] = useState<string>(format(startOfToday(), datetimeInputFormat));
  const [endDate, setEndDate] = useState<string>(format(endOfToday(), datetimeInputFormat));
  const [notLoadedMode, setReportNotLoadedMode] = useState('2');
  const [templateRepType, setTemplateRepType] = useState<SecurityReportTypes>();

  const { isShow, openModal, closeModal } = useModal();
  const { loading, error, pictures } = useSelector(repPhotoSelector);

  const debStartDate = useDebounce(startDate, 1500);
  const debEndDate = useDebounce(endDate, 1500);
  const debNotLoadedMode = useDebounce(notLoadedMode, 1500);
  const debReportType = useDebounce(reportType);
  const date = format(new Date(), 'dd.LL.yyyy HH:mm');

  const mode = history.location.pathname.split('/')[1];
  const isSecurity = mode === 'security';
  const isDirector = mode === 'director';

  const Component = useMemo(() => {
    if (reportComponent) {
      // @ts-ignore
      if (isSecurity) return reportCompSecurity[reportComponent];
      // @ts-ignore
      if (isDirector) return directorCompSecurity[reportComponent];
      // @ts-ignore
      return reportCompOrder[reportComponent];
    }
  }, [reportComponent, mode]);

  const { reports: unloadingReports } = useSelector(
    (state: RootState) => state.securityModuleReducer.reportsReducer.reportUnloadingReducer,
  );

  const { reports: loadingReports } = useSelector(
    (state: RootState) => state.securityModuleReducer.reportsReducer.reportLoadingReducer,
  );

  function prepareDataForExportUnloading(reports: any) {
    return reports.map((report: any) => ({
      Рейс: report.tripId,
      'Дата бронювання': report.bookingDate,
      'Держномер ТЗ': report.vehicleLicensePlate,
      'Держномер причепа': report.trailerLicensePlate,
      Водій: report.driverName,
      'Номер посвідчення': report.driverDocumentNumber,
      Телефон: report.driverPhone,
      "Дані в'їзду": report.entryDateTime,
      'Дані виїзду': report.departureDateTime,
      "Вага в'їзду, т": report.entryWeight / 1000,
      'Вага виїзду, т': report.departureWeight / 1000,
      'Деталі вантажу': report.packingLists,
      'Сума по TTH, т': report.ttnSum,
      'Вивантажено, т': (report.entryWeight - report.departureWeight) / 1000,
      'Відхилення, т': ((report.entryWeight - report.departureWeight - report.ttnSum) * 100) / 100000,
      Експедитор: report.forwarderName,
      'Телефон Екс': report.forwarderPhone,
      'Коментар охорони': report.securityMessage,
      'Номер Заводу': report.factoryNumber,
      'Номер Складу': report.warehouseCode,
      'Номер вихідного постачання': report.outputSupplyNumber,
      'Кількість позицій у наряді': report.orderItemsCount,
    }));
  }
  function prepareDataForExportLoading(reports: any) {
    return reports.map((report: any) => ({
      Рейс: report.tripId,
      'Дата бронювання': report.bookingDate,
      'Тип перевізника': report.сarrierTypeStr,
      'Держномер ТЗ': report.vehicleLicensePlate,
      'Держномер причепа': report.trailerLicensePlate,
      Водій: report.driverName,
      'Номер посвідчення': report.driverDocumentNumber,
      Телефон: report.driverPhone,
      "Дані в'їзду": report.entryDateTime,
      'Дані виїзду': report.departureDateTime,
      "Вага в'їзду, т": report.entryWeight / 1000,
      'Сума по нарядам, НЕТТО, т (SAP)': report.ordersCalculatedNetWeight / 1000,
      'Сума по нарядам, БРУТТО, т': report.ordersCalculatedGrossWeight / 1000,
      'Вага перфорації, т': report.perforationWeight / 1000,
      'Вага виїзду, т': report.departureWeight / 1000,
      'Відхилення, т': report.allowedWeight / 1000,
      'Відхилення, %': report.weightDeviationInPercent,
      Замовник: report.customer,
      Експедитор: report.forwarderName,
      'Телефон Екс': report.forwarderPhone,
      'Коментар охорони': report.securityMessage,
      'Коментар завсклада': report.directorMessage,
      'Номер Заводу': report.factoryNumber,
      'Номер Складу': report.warehouseCode,
      'Номер вихідного постачання': report.outputSupplyNumber,
      'Кількість позицій у наряді': report.orderItemsCount,
    }));
  }

  const handleSelect = (eventKey: any) => {
    setReportType(eventKey);
    if (isShow) closeModal();
  };

  const onAceptChange = (eventKey: any) => {
    setTemplateRepType(eventKey);
    openModal();
  };

  const onRowDoubleClick = (rowData: DefaultRecordType) => {
    const { tripId } = rowData;

    return {
      onDoubleClick: () => dispatch(getRepTripRequest(tripId)),
    };
  };

  const ExportExcel = ({ excelData, fileName }: any) => {
    const exportToExcel = async (file: string) => {
      const jsonData = JSON.parse(excelData);
      const headers = Object.keys(jsonData[0]);
      const rows = jsonData.map((data: any) => headers.map((header) => data[header]));
      try {
        const resp: AxiosResponse<any> = await axios.post(
          'Reports/toExcel',
          {
            filename: btoa(encodeURIComponent(file)),
            headers: btoa(encodeURIComponent(JSON.stringify(headers))),
            rows: btoa(encodeURIComponent(JSON.stringify(rows))),
          },
          {
            responseType: 'blob',
          },
        );

        const url = window.URL.createObjectURL(new Blob([resp.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `${file}.xlsx`);
        document.body.appendChild(link);
        link.click();
      } catch (e) {
        console.log(e);
      }
    };

    return (
      <Button
        variant="light"
        type="button"
        className="ml-2 not-print"
        onClick={() => exportToExcel(fileName)}
        disabled={!Component}
      >
        <ExcelIcon className={styles.plusIcon} />
        &nbsp;Зберегти в Excel
      </Button>
    );
  };

  let ExcelExportData;
  let FileName;

  if (reportType === SecurityReportTypes.loading) {
    ExcelExportData = JSON.stringify(prepareDataForExportLoading(loadingReports));
    FileName = `Історія проходу ТЗ: завантаження ${date}`;
  } else if (reportType === SecurityReportTypes.unloading) {
    ExcelExportData = JSON.stringify(prepareDataForExportUnloading(unloadingReports));
    FileName = `Історія проходу ТЗ: розвантаження ${date}`;
  }

  useEffect(() => {
    if (
      debStartDate === startDate &&
      debEndDate === endDate &&
      debReportType === reportType &&
      debNotLoadedMode === notLoadedMode
    ) {
      setForceUpdate(!forceUpdate);
      setReportComponent(reportType);
    }
  }, [debStartDate, debEndDate, debReportType, debNotLoadedMode]);

  return (
    <Container fluid>
      <div className="pt-3 pb-2 d-flex align-items-center p-15 pr-0 position-relative">
        <span className="not-print">Тип звіту:</span>
        <DropdownButton
          variant="outline-secondary btn-reports"
          title={reportType || 'Тип звіту'}
          as={ButtonGroup}
          className={`ml-2 not-print ${!reportType ? 'bg-warning' : ''}`}
        >
          {Object.entries(isSecurity || isDirector ? SecurityReportTypes : OrderReportTypes).map(
            ([key, value]: [string, string]) => (
              <Dropdown.Item
                key={key}
                eventKey={value}
                // eslint-disable-next-line
                onSelect={!reportType ? handleSelect : reportType && reportType !== value ? onAceptChange : () => false}
              >
                {value}
              </Dropdown.Item>
            ),
          )}
        </DropdownButton>
        <span className="for-print text-uppercase">{reportType}:</span>
        {/* @ts-ignore */}
        {debReportType && <span className="ml-2">({secReportComment[debReportType]})</span>}
        <span className="ml-2 for-print">
          ({format(new Date(debStartDate), 'dd.LL.yyyy')}-{format(new Date(debEndDate), 'dd.LL.yyyy')})
        </span>
        {debReportType && debReportType !== 'Чорний список' && debReportType !== 'Звіт 2359' && (
          <div className="form-inline p-0 date-filter not-print">
            <FormControl
              type="datetime-local"
              name="startDate"
              onChange={(e) => setStartDate(e.target.value)}
              value={startDate}
              className="setAutoWith"
            />

            <FormControl
              type="datetime-local"
              name="endDate"
              className="ml-2 setAutoWith"
              onChange={(e) => setEndDate(e.target.value)}
              value={endDate}
            />
            {debReportType === 'Історія проходу ТЗ: завантаження' && (
              <div className="ml-4">
                Ознака завантаження
                <Select onChange={(e) => setReportNotLoadedMode(e)} value={notLoadedMode} className="ml-2">
                  <Select.Option value="0">Вантажився</Select.Option>
                  <Select.Option value="1">Не вантажився</Select.Option>
                  <Select.Option value="2">Будь-який</Select.Option>
                </Select>
              </div>
            )}
          </div>
        )}
        {reportType && (
          <Col className="text-right d-flex flex-row justify-content-end align-items-center">
            <span className="text-danger">Звіт згенеровано: {date}</span>
            <ExportExcel excelData={ExcelExportData} fileName={FileName} />
          </Col>
        )}
      </div>
      <Row>
        <Col key="row-rc-1">
          {loading && (
            <div className="reports-loader">
              <SmallSpinner />
            </div>
          )}
          {!!Component && (
            <Component
              key="row-rc-2"
              rerender={forceUpdate}
              startDate={debStartDate}
              endDate={debEndDate}
              notLoadedMode={debNotLoadedMode}
              onRow={onRowDoubleClick}
            />
          )}
        </Col>
      </Row>

      <YesNoModal
        isShow={isShow}
        title="При переході звіт не буде збережено. Перейти"
        acceptHandler={() => handleSelect(templateRepType)}
        closeModal={closeModal}
      />
      {/* @ts-ignore */}
      {pictures && pictures.length ? <PhotoGrid mode="reports" statusId={99} serverImages={pictures} /> : ''}
      <ErrorMsg isShow={!!error} error={error || ''} closeHandler={() => dispatch(clearReportPhoto())} />
    </Container>
  );
};
